import { CommonModule } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatSelectModule } from "@angular/material/select";
import { Api } from "../../services/api";
import { Router } from "@angular/router";
import { BaseChartDirective } from 'ng2-charts';
import { Chart, ChartConfiguration, ChartOptions, registerables } from 'chart.js';

@Component({
  selector: 'app-forecast-graphic',
  standalone: true,
  imports: [
    CommonModule,
    MatSelectModule,
    MatButtonModule,
    MatDividerModule,
    FormsModule,
    MatDialogModule,
    BaseChartDirective,
  ],
  templateUrl: './forecast-graphic.html',
  styleUrl: './forecast-graphic.css'
})
export class ForecastGraphic implements OnInit{

  private api = inject(Api);
  isLoading: boolean = false;
  public forecasts: any[] = [];
  public totales: any = [];

  selectedItem: string = 'todas';
  showRecentDropdown: boolean = false;
  errorMessage: string = '';
  expandedMonth: string | null = null;
  selectedExecutive: string | null = null; 
  selectedOpportunities: any[] = [];

  public chartData: ChartConfiguration<'bar'>['data'] = {
    labels: [
      'Octubre', 'Noviembre', 'Diciembre', 'Enero', 'Febrero', 'Marzo', 
      'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre'
    ],
    datasets: [
      {
        label: 'Metas',
        data: [50, 45, 105, 185, 100, 45, 45, 45, 100, 25, 35, 45],
        backgroundColor: '#D32F2F',
        borderColor: '#D32F2F',
        borderWidth: 1
      },
      {
        label: 'Oportunidades Cerradas',
        data: [130, 155, 50, 55, 185, 120, 130, 155, 50, 55, 185, 120],
        backgroundColor: '#FF7043',
        borderColor: '#FF7043', 
        borderWidth: 1
      },
      {
        label: 'Mejor de los casos',
        data: [80, 80, 80, 85, 175, 80, 90, 80, 105, 80, 25, 80],
        backgroundColor: '#F5DEB3',
        borderColor: '#F5DEB3',
        borderWidth: 1
      },
      {
        label: 'Oportunidades de baja probabilidad',
        data: [85, 90, 95, 100, 105, 85, 95, 80, 80, 25, 45, 155],
        backgroundColor: '#FF8A65',
        borderColor: '#FF8A65',
        borderWidth: 1
      }
    ]
  };

  public chartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        font: {
          size: 16,
          weight: 'bold'
        },
        color: '#FF7043',
        align: 'start',
        padding: {
          bottom: 30
        }
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: $${context.parsed.y}M CLP`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 200,
        ticks: {
          stepSize: 20,
          callback: function(value) {
            return `${value}M`;
          }
        },
        title: {
          display: true,
          text: 'Importe del pronóstico',
          font: {
            size: 12
          }
        }
      },
      x: {
        ticks: {
          maxRotation: 0,
          font: {
            size: 11
          }
        }
      }
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  constructor(
    private router: Router
  ) {
    Chart.register(...registerables);
  }

  ngOnInit(): void {
    this.getForecastReport();
    this.loadForecastData();
  }

  loadForecastData(): void {
    // Por ahora datos dummy, luego conectar con API
    this.forecasts = [
      {
        categoria: 'Metas',
        semana1: 40,
        semana2: 85,
        variacion: '+112%'
      },
      {
        categoria: 'Oportunidades cerradas',
        semana1: 130,
        semana2: 95,
        variacion: '-27%'
      },
      // ... más datos
    ];
  }

  updateChartData(data: any[]): void {
    // Extraer labels (nombres de los meses)
    const labels = data.map(item => {
      // Extraer solo el mes de "Enero 2025", "Febrero 2025", etc.
      return item.mes_largo.split(' ')[0];
    });

    // Función helper para formatear montos a millones
    const formatAmountToMillions = (amount: number): number => {
      return Math.round(amount / 1000000 * 100) / 100; // Redondear a 2 decimales
    };

    // Extraer datos para cada dataset
    const metasData = data.map(item => formatAmountToMillions(item.total_metas));
    const cerradasGanadasData = data.map(item => formatAmountToMillions(item.cerradas_ganadas));
    const altaProbabilidadData = data.map(item => formatAmountToMillions(item.alta_probabilidad));
    const bajaProbabilidadData = data.map(item => formatAmountToMillions(item.baja_probabilidad));

    // Actualizar el chartData
    this.chartData = {
      labels: labels,
      datasets: [
        {
          label: 'Metas',
          data: metasData,
          backgroundColor: '#D32F2F',
          borderColor: '#D32F2F',
          borderWidth: 1
        },
        {
          label: 'Oportunidades Cerradas',
          data: cerradasGanadasData,
          backgroundColor: '#FF7043',
          borderColor: '#FF7043', 
          borderWidth: 1
        },
        {
          label: 'Alta Probabilidad',
          data: altaProbabilidadData,
          backgroundColor: '#F5DEB3',
          borderColor: '#F5DEB3',
          borderWidth: 1
        },
        {
          label: 'Baja Probabilidad',
          data: bajaProbabilidadData,
          backgroundColor: '#FF8A65',
          borderColor: '#FF8A65',
          borderWidth: 1
        }
      ]
    };

    console.log('Chart data actualizado:', this.chartData);
  }

  getForecastReport(): void {
    this.api.showLoading();
    this.api.getForecastReport({ year: ""}).subscribe({
      next: (response: any) => {
        this.api.hideLoading();
        this.forecasts = response.data;
        this.totales = response.total_anual;
        console.log('Informe de pronóstico recibido:', this.forecasts);
        
        // Actualizar el gráfico con los datos reales
        this.updateChartData(response.data);
      },
      error: (error: any) => {
        this.api.hideLoading();
        this.errorMessage = 'Error al obtener el informe de pronóstico.';
      }
    }); 
  }

 toggleRecentDropdown(): void {
    this.showRecentDropdown = !this.showRecentDropdown;
  }

  selectItem(key: string): void {
    this.selectedItem = key;
    this.showRecentDropdown = false;
  }

  sum(key: keyof typeof this.totales) {
    return this.forecasts.reduce((acc, item) => acc + (item[key] || 0), 0);
  }

  toggleMonth(mes: string) {
    this.expandedMonth = this.expandedMonth === mes ? null : mes;
  }

  showExecutiveDetails(exec: any) {
    this.api.showLoading();
    this.api.getOpportunityByEjecutivo({ ejecutivo_id: exec.ejecutivo_id }).subscribe({
      next: (response: any) => {
        this.api.hideLoading();
        this.selectedOpportunities = response.data;
        console.log('Oportunidades recibidas:', response.data);
      },
      error: (error: any) => {
        this.api.hideLoading();
      }
    });
  
     this.selectedExecutive = exec.ejecutivo;

  }


  showTableView() {
    this.router.navigate(['/forecast']);
  }

  formatAmountToMillions(amount: number): string {
    if (!amount || amount === 0) return '0';
    const millions = amount / 1000000;
    return millions.toFixed(0);
  }

  
}